import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GradedCanvas from './GradedCanvas';
import GradePanel from './GradePanel';
import MaskPanel from './MaskPanel';
import Scopes from './Scopes';
import SplitPreview from './SplitPreview';
import SocialExportPanel from './SocialExportPanel';
import SubjectPathLane from './SubjectPathLane';
import FrameTrackStrip from './FrameTrackStrip';
import ColorAssistant from './ColorAssistant';
import {
  useGradePresets,
  useExports,
  useClipReframes,
  addGradePreset,
  deleteGradePreset,
  updateMovie,
  requestMaster,
  requestClipReframe,
} from '../data/firestore';
import { DEFAULT_GRADE, DEFAULT_MASK_ADJ, DEFAULT_MASK_RANGE, clone, gradeSignature, composeGrade, activeLayers, autoGrade, PRESETS, stampPreset, isIdentityGrade, lutFromCubeText } from '../utils/grade';
import { useAgentHandlers, useAgentContext } from '../agent/agentBus';
import { resolveLook, applyLookGrade, adjustGrade, presetNames } from '../agent/colorActions';
import { cropRectFor, waypointToXY, centerAt, scaleAt, densifyWaypoints, anchoredDenseWaypoints, thinPath, ASPECT_RATIOS } from '../utils/social';
import { jobEtaLabel, estimateMasterSec, estimateReframeSec, fmtDuration, useNow } from '../utils/eta';
import {
  fmtTime,
  masterDuration,
  masterSegments,
  clipIndexAtMaster,
  masterCutSig,
  clipReframe,
  DEFAULT_CLIP_REFRAME,
} from '../utils/movie';

const SIDE_W = 1100;

// The TRUE raw baseline: the default grade with NO picture profile (the
// 'adobe-color' default applies a gentle contrast S-curve). Used for the
// "Original" pane and as the starting grade so an ungraded movie previews
// exactly like its source footage and its (ungraded) published output — the
// publish path skips identity grades, so a non-neutral studio baseline would
// otherwise make every clip look more contrasty here than it actually exports.
const RAW_GRADE = { ...clone(DEFAULT_GRADE), profile: 'neutral' };

// The crop preview can target any of the platform aspect ratios.
const ASPECTS = ASPECT_RATIOS;

// Distinct colors per detected subject so multiple people/surfers in a clip
// are easy to tell apart in the overlay, chips, and path lane.
const TRACK_COLORS = ['#2dd4bf', '#f59e0b', '#a78bfa', '#f472b6', '#38bdf8', '#84cc16'];
const trackColor = (i) => TRACK_COLORS[i % TRACK_COLORS.length];

// Interpolate a detected track's box at time t (for the tracking overlay).
function interpTrack(track, t) {
  const s = track.samples || [];
  if (!s.length) return null;
  if (t < s[0].t - 0.6 || t > s[s.length - 1].t + 0.6) return null;
  if (t <= s[0].t) return s[0];
  const last = s[s.length - 1];
  if (t >= last.t) return last;
  for (let i = 1; i < s.length; i += 1) {
    if (t <= s[i].t) {
      const a = s[i - 1];
      const b = s[i];
      const f = (t - a.t) / (b.t - a.t || 1);
      return {
        cx: a.cx + (b.cx - a.cx) * f,
        cy: a.cy + (b.cy - a.cy) * f,
        w: a.w + (b.w - a.w) * f,
        h: a.h + (b.h - a.h) * f,
      };
    }
  }
  return last;
}

// Embeddable color + reframe workspace: one master, a persistent
// original-vs-composite split, and a stack of toggleable layers (Light / Color
// / Effects / Reframe). Each enabled layer composites onto the right pane in
// real time. Rendered inside the unified movie Editor; the parent owns the
// editbar / save / publish. `initialTab` = 'grade' | 'reframe'.
export default function ColorReframeStudio({ id, movie, clips: clipsProp, onClipReframe, initialTab = 'grade', onTime, seekRef, ctlRef, onPlayingChange, footer, initialTime = 0, initialPlaying = false, sideHidden = false, onToggleSide }) {
  const { docs: presets } = useGradePresets();
  const { docs: exports } = useExports(id);

  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [grade, setGrade] = useState(() => clone(RAW_GRADE));
  const [activeMaskId, setActiveMaskId] = useState(null);
  const [stats, setStats] = useState(null);
  const [origStats, setOrigStats] = useState(null); // histogram of the untouched master
  // No autoplay-on-entry: the transport state carries over from the previous
  // tab (see the videoEl effect below, which replaces the old autoPlay attr).
  const [playing, setPlaying] = useState(!!initialPlaying);
  const [err, setErr] = useState('');
  const [editor, setEditor] = useState(initialTab === 'reframe' ? 'reframe' : 'grade'); // 'grade' | 'reframe'
  const [layers, setLayers] = useState({ light: true, color: true, effects: true, reframe: false });
  const [aspect, setAspect] = useState('9x16');
  const [t, setT] = useState(Math.max(0, initialTime || 0));
  const [showTrack, setShowTrack] = useState(true); // YOLO path + sample nodes overlay
  const [showPath, setShowPath] = useState(true); // subject path lane under the preview
  const [showFrames, setShowFrames] = useState(false); // per-clip frames + tracking contact sheet
  const [pathAxis, setPathAxis] = useState('cx'); // lane axis: 'cx' (horizontal) | 'cy' (vertical)
  // Auto-track keyframe spacing (seconds). Higher = sparser/easier-to-edit path;
  // lower = denser/closer to every detection. Persisted across clips + reloads.
  const [kfDensity, setKfDensity] = useState(() => {
    try { const v = Number(localStorage.getItem('cs_kf_density')); return v >= 0.2 && v <= 3 ? v : 0.6; } catch { return 0.6; }
  });
  const [hoverTrackId, setHoverTrackId] = useState(null); // emphasize a track on chip hover
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'single'
  // ---- Per-clip reframe model -------------------------------------------------
  // Crops are CLIP-specific: each clip carries its own crop window (center / zoom
  // / subject) under clip.reframe. The output RATIO (`aspect`) is chosen per
  // export and applies movie-wide (one social file = one aspect). The studio
  // plays the composed master; the playhead picks the active clip (dissolve-
  // aware), and every crop edit targets that clip only — like per-clip grading.
  const clips = useMemo(
    () => (Array.isArray(clipsProp) && clipsProp.length ? clipsProp : (movie?.clips || [])),
    [clipsProp, movie],
  );
  const segs = useMemo(() => masterSegments(clips), [clips]);
  const [segIndex, setSegIndex] = useState(0);
  const activeClip = clips[segIndex] || clips[0] || null;
  const activeClipId = activeClip?.id || null;
  // The active clip's tile window in master time (its subject waypoints + tracks
  // live here). Defined early because per-clip track derivation reads it.
  const activeSeg = segs[segIndex] || segs[0] || null;

  // Working copy of the ACTIVE clip's reframe — instant preview while dragging,
  // persisted (debounced) to that clip via onClipReframe. `manual` / `cropMode`
  // are aliases so the existing drag/zoom handlers keep reading the same shape.
  const [rf, setRf] = useState(DEFAULT_CLIP_REFRAME);
  const manual = rf.manualCrop;
  const cropMode = rf.cropMode;
  const zoomKeys = rf.zoomKeys || [];
  const reEdit = { subjectTrackId: rf.subjectTrackId, secondaryTrackIds: rf.secondaryTrackIds || [], waypoints: rf.waypoints };
  // EMA alpha for the subject pan/zoom smoothing (1 = follow keyframes exactly,
  // lower = smoother glide). Surfaced as a 0..100% "smoothness" slider.
  const smoothAlpha = Math.min(1, Math.max(0.05, Number(rf.smoothing) || 0.35));
  const smoothPct = Math.round((1 - (smoothAlpha - 0.05) / 0.95) * 100);

  const gradeHydrated = useRef(false);
  const gradeSaveTimer = useRef(null);
  const reSaveTimer = useRef(null);
  const lutInputRef = useRef(null);
  const wrapRef = useRef(null);
  // Measure the stage so the preview can scale to fit its actual height instead
  // of clipping. The Reframe layout shares this column with the subject-path
  // lanes + transport, so the stage is much shorter than the Color tab's — a
  // viewport cap (70vh) was too tall and overflow:hidden cropped the canvas
  // top/bottom, making the preview look wider (e.g. 2.15:1 vs the true 1.78:1).
  const stageRef = useRef(null);
  const [stageH, setStageH] = useState(0);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setStageH(el.clientHeight || 0));
    ro.observe(el);
    setStageH(el.clientHeight || 0);
    return () => ro.disconnect();
  }, []);

  const master = movie?.master || {};
  const masterUrl = master.status === 'ready' ? master.url : '';
  // Newer masters are fully RAW (no per-clip looks baked) — so the studio can
  // apply each clip's color/light look live in WebGL, exactly matching the Color
  // tab and the published/social output. Older (baked) masters carry the looks
  // already, so we leave the live base off to avoid double-grading them.
  const masterRaw = master.raw === true;

  // Subject tracking is PER-CLIP: each clip carries its own analysis on
  // movies/{id}/clipReframe/{clipId}. The active clip (under the playhead) drives
  // the panel. Movies analyzed movie-wide before this fall back to movie.reframe,
  // sliced to the active clip's tile so they keep working until re-analyzed.
  const clipReframes = useClipReframes(id);
  const movieReframe = movie?.reframe || {};
  const clipRe = (activeClipId && clipReframes[activeClipId]) || null;
  const legacyTracks = !clipRe && Array.isArray(movieReframe.tracks) && movieReframe.tracks.length > 0;
  const reframe = clipRe || (legacyTracks ? movieReframe : {});
  const reStatus = reframe.status;
  const reActive = reStatus === 'queued' || reStatus === 'analyzing';
  const reReady = reStatus === 'ready';
  // This clip's analyzed window no longer matches its current tile length
  // (trim / speed / order changed) → its tracks are stale.
  const clipReStale =
    !!clipRe && clipRe.status === 'ready' && !!activeSeg && clipRe.t1 != null &&
    Math.abs((clipRe.t1 - clipRe.t0) - (activeSeg.tileEnd - activeSeg.tileStart)) > 0.75;
  const masterActive = master.status === 'composing' || master.status === 'queued';
  const anyClipAnalyzing =
    movieReframe.status === 'analyzing' ||
    Object.values(clipReframes).some((c) => c && (c.status === 'analyzing' || c.status === 'queued'));
  const now = useNow(reActive || masterActive || anyClipAnalyzing);

  const srcW = (clipRe && clipRe.srcW) || movieReframe.srcW || master.w || 1920;
  const srcH = (clipRe && clipRe.srcH) || movieReframe.srcH || master.h || 1080;
  // Tracks shown for the ACTIVE clip: its own analysis, or (legacy) the
  // movie-wide tracks filtered to this clip's tile window.
  const tracks = useMemo(() => {
    if (clipRe && Array.isArray(clipRe.tracks)) return clipRe.tracks;
    if (!legacyTracks) return [];
    const t0 = activeSeg ? activeSeg.tileStart : 0;
    const t1 = activeSeg ? activeSeg.tileEnd : Infinity;
    return (movieReframe.tracks || [])
      .map((tr) => ({ ...tr, samples: (tr.samples || []).filter((p) => p.t >= t0 - 0.5 && p.t <= t1 + 0.5) }))
      .filter((tr) => tr.samples.length);
  }, [clipRe, legacyTracks, movieReframe, activeSeg]);

  // Refs the per-frame crop getter / overlay read so they see latest state.
  const srcRef = useRef({ srcW, srcH });
  const aspectRef = useRef(aspect);
  const segsRef = useRef(segs);
  const rfRef = useRef(rf);
  const activeIdRef = useRef(activeClipId);
  const manualRef = useRef(manual);
  // Pre-smoothed (EMA) center path for the ACTIVE clip, so the live right-pane
  // crop glides exactly like the export instead of snapping linearly between
  // keyframes. Rebuilt whenever the path or smoothing amount changes.
  const smoothPathRef = useRef([]);
  srcRef.current = { srcW, srcH };
  aspectRef.current = aspect;
  segsRef.current = segs;
  rfRef.current = rf;
  activeIdRef.current = activeClipId;

  useEffect(() => {
    const wps = rf.waypoints || [];
    if (rf.cropMode !== 'subject' || wps.length < 2 || !activeSeg) {
      smoothPathRef.current = [];
      return;
    }
    // Bake parity: the publish densifies a track-locked path with the raw YOLO
    // samples (keyframes stay authoritative anchors), so preview the same blend.
    const track = rf.subjectTrackId ? tracks.find((t) => t.id === rf.subjectTrackId) : null;
    const path = track ? anchoredDenseWaypoints(track.samples || [], wps) : wps;
    smoothPathRef.current = densifyWaypoints(path, (activeSeg.tileEnd || 0) + 0.5, 30, smoothAlpha);
  }, [rf.waypoints, rf.cropMode, rf.subjectTrackId, tracks, smoothAlpha, activeSeg]);
  manualRef.current = manual;

  // Follow the parent tab (Color & Light vs Reframe).
  useEffect(() => {
    setEditor(initialTab === 'reframe' ? 'reframe' : 'grade');
  }, [initialTab]);

  // Hydrate the grade once.
  useEffect(() => {
    if (movie && !gradeHydrated.current) {
      gradeHydrated.current = true;
      // Only adopt a real (non-identity) saved grade. An identity grade (incl. a
      // legacy all-zero 'adobe-color' default) is treated as "no grade", so the
      // studio starts raw/neutral and matches the source + ungraded export.
      if (movie.grade && !isIdentityGrade(movie.grade)) setGrade(clone(movie.grade));
    }
  }, [movie]);

  // Flush any debounced saves if the user leaves the tab quickly.
  useEffect(() => () => {
    if (gradeSaveTimer.current) clearTimeout(gradeSaveTimer.current);
    if (reSaveTimer.current) clearTimeout(reSaveTimer.current);
  }, []);

  // Load the active clip's stored reframe into the working copy when it changes.
  useEffect(() => {
    setRf(clipReframe(activeClip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClipId]);

  // Commit a new working reframe: instant local preview + debounced persist to
  // the active clip. Never touches any other clip.
  const commitReframe = (next) => {
    setRf(next);
    rfRef.current = next;
    if (!activeClipId) return;
    if (reSaveTimer.current) clearTimeout(reSaveTimer.current);
    reSaveTimer.current = setTimeout(() => { if (onClipReframe) onClipReframe(activeClipId, next); }, 250);
  };

  // Connect the <video> to the canvases once it can render.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onReady = () => setVideoEl(v);
    v.addEventListener('loadeddata', onReady);
    if (v.readyState >= 2) onReady();
    return () => v.removeEventListener('loadeddata', onReady);
  }, [masterUrl]);

  // One-shot: land the master at the movie time carried over from the previous
  // tab (tab switches remount the studio, which otherwise starts at 0).
  const initialSeekRef = useRef(initialTime > 0 ? initialTime : null);
  useEffect(() => {
    if (!videoEl) return;
    const pending = initialSeekRef.current;
    if (pending == null) return;
    initialSeekRef.current = null;
    try { videoEl.currentTime = Math.max(0, pending); } catch { /* noop */ }
    setT(pending);
    const i = clipIndexAtMaster(segsRef.current, pending);
    if (i >= 0) setSegIndex(i);
  }, [videoEl]);

  // Apply the carried transport state once the master can render (replaces the
  // old unconditional autoPlay attr — a paused tab stays paused here), and
  // again after a master reload so an in-flight playback keeps rolling.
  useEffect(() => {
    if (!videoEl) return;
    if (playing) videoEl.play().catch(() => setPlaying(false));
    else { try { videoEl.pause(); } catch { /* noop */ } }
    // `playing` intentionally NOT a dep: user toggles drive the element via
    // togglePlay/pauseCtl; this only syncs the state onto a (re)loaded master.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEl]);

  // The preview master is built only on explicit request now (Reframe needs a
  // flat composite for subject tracking + social crops). We surface a build
  // prompt below rather than auto-spending render compute on every visit.
  // Compare the master's baked length against the live cut's DISSOLVE-AWARE
  // length (the same total render.js builds the master to). A clip speed change
  // always moves this number, so a stale master — e.g. one built before slow-mo
  // was set in Assemble, which plays the clips too fast — is reliably flagged.
  // (The old check used the hard-cut movieDuration, which ignored dissolves and
  // gave chronic false positives, so the rebuild prompt got tuned out.)
  const masterWant = masterDuration(clips);
  const masterHave = master.durationSec || 0;
  // Second tell beyond duration: the composition fingerprint stamped when the
  // build was requested. Catches reorders / in-out slips that keep the length.
  const masterStale =
    master.status === 'ready' &&
    ((masterWant > 0 && masterHave > 0 && Math.abs(masterWant - masterHave) > 0.75) ||
      (master.cutSig ? master.cutSig !== masterCutSig(clips) : false));
  const masterEtaSec = estimateMasterSec(masterWant);

  // HARD GUARD: a stale master's clip timing no longer matches the cut, so
  // letting it roll would desync the shared timeline and poison tracking /
  // crops. Park the transport — the banner above the stage offers the rebuild.
  useEffect(() => {
    if (!masterStale) return;
    const v = videoRef.current;
    if (v && !v.paused) { try { v.pause(); } catch { /* noop */ } }
    setPlaying(false);
  }, [masterStale]);
  // Per-clip analysis estimate: just the active clip's tile length, plus the
  // master compose if it isn't built yet (the first analysis triggers it).
  const clipTileSec = activeSeg ? (activeSeg.tileEnd - activeSeg.tileStart) : masterWant;
  const reframeEtaSec = estimateReframeSec(clipTileSec) + (masterUrl ? 0 : masterEtaSec);

  // Drive the tracking overlay playhead + report master time to the parent
  // (so the shared timeline's playhead tracks studio playback). Throttled to
  // ~12fps to avoid 60fps React re-renders.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    let raf;
    let lastUi = 0;
    const loop = (ts) => {
      if (ts - lastUi > 80) {
        lastUi = ts;
        const ct = v.currentTime || 0;
        setT(ct);
        const i = clipIndexAtMaster(segsRef.current, ct);
        if (i >= 0) setSegIndex((prev) => (prev === i ? prev : i));
        if (onTime) onTime(ct);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [videoEl, onTime]);

  // Let the parent (shared timeline) seek the master.
  useEffect(() => {
    if (!seekRef) return undefined;
    seekRef.current = (sec) => {
      const v = videoRef.current;
      if (!v) return;
      try { v.currentTime = Math.max(0, sec); } catch { /* noop */ }
      setT(sec);
      const i = clipIndexAtMaster(segsRef.current, sec);
      if (i >= 0) setSegIndex((prev) => (prev === i ? prev : i));
    };
    return () => {
      if (seekRef) seekRef.current = null;
    };
  }, [seekRef, videoEl]);

  const saveGrade = (next) => {
    setGrade(next);
    if (gradeSaveTimer.current) clearTimeout(gradeSaveTimer.current);
    gradeSaveTimer.current = setTimeout(() => updateMovie(id, { grade: next }).catch(() => {}), 700);
  };

  // ---- local masks (color stage) ------------------------------------------
  // Same masks the photo editor uses — radial / gradient, baked per-clip by the
  // renderer's geq chain and rendered by GradedCanvas, so preview == export. The
  // "Follow subject" snap places a mask on the tracked subject at the current
  // frame (positions can then be fine-tuned with the sliders).
  const maskList = grade.masks || [];
  const newMask = (type) => (type === 'linear'
    ? { id: `m${Date.now().toString(36)}`, type: 'linear', cx: 0.5, cy: 0.5, rx: 0.3, ry: 0.3, angle: 90, feather: 0.4, invert: false, amount: 1, range: { ...DEFAULT_MASK_RANGE }, adj: { ...DEFAULT_MASK_ADJ, exposure: -0.3 } }
    : { id: `m${Date.now().toString(36)}`, type: 'radial', cx: 0.5, cy: 0.5, rx: 0.28, ry: 0.3, feather: 0.6, invert: false, amount: 1, range: { ...DEFAULT_MASK_RANGE }, adj: { ...DEFAULT_MASK_ADJ, exposure: 0.3 } });
  const addMaskUi = (type) => {
    const m = newMask(type);
    saveGrade({ ...grade, masks: [...maskList, m] });
    setActiveMaskId(m.id);
  };
  const patchMaskUi = (mid, patch) =>
    saveGrade({ ...grade, masks: maskList.map((m) => (m.id === mid ? { ...m, ...patch } : m)) });
  const patchMaskAdjUi = (mid, key, val) =>
    saveGrade({ ...grade, masks: maskList.map((m) => (m.id === mid ? { ...m, adj: { ...(m.adj || {}), [key]: val } } : m)) });
  const deleteMaskUi = (mid) =>
    saveGrade({ ...grade, masks: maskList.filter((m) => m.id !== mid) });
  const snapMaskToSubject = (mid) => {
    const tt = videoRef.current ? videoRef.current.currentTime : 0;
    const sp = smoothPathRef.current;
    const wp = reEdit.waypoints || [];
    const c = (sp && sp.length) ? centerAt(sp, tt) : (wp.length ? centerAt(wp, tt) : null);
    if (c) patchMaskUi(mid, { cx: c.cx, cy: c.cy });
  };

  // Capture the current master (ungraded) + graded preview frames as downscaled
  // JPEG data URLs for the AI color assistant. Pulls straight from the on-screen
  // WebGL canvases (preserveDrawingBuffer) so the model sees exactly what I see.
  const captureFrames = useCallback(async () => {
    const out = [];
    const tlabel = `t=${(videoRef.current?.currentTime || 0).toFixed(1)}s`;
    const shrink = (srcCanvas, label) => {
      const w = srcCanvas?.width || 0;
      const h = srcCanvas?.height || 0;
      if (!w || !h) return null;
      const s = Math.min(1, 1024 / w);
      const oc = document.createElement('canvas');
      oc.width = Math.max(1, Math.round(w * s));
      oc.height = Math.max(1, Math.round(h * s));
      const ctx = oc.getContext('2d');
      if (!ctx) return null;
      try {
        ctx.drawImage(srcCanvas, 0, 0, oc.width, oc.height);
        return { label, dataUrl: oc.toDataURL('image/jpeg', 0.85) };
      } catch {
        return null;
      }
    };
    const scope = stageRef.current || document;
    const masterC = scope.querySelector('.cs-cap-master');
    const gradedC = scope.querySelector('.cs-cap-graded');
    const fm = masterC && shrink(masterC, `original master · ${tlabel}`);
    if (fm) out.push(fm);
    const fg = gradedC && shrink(gradedC, `current graded · ${tlabel}`);
    if (fg) out.push(fg);
    return out;
  }, []);

  // ---- Voice copilot wiring ------------------------------------------------
  const assistantRef = useRef(null);
  useAgentHandlers({
    apply_look: ({ name }) => {
      const p = resolveLook(name, presets || []);
      if (!p) return { ok: false, error: `no look matching "${name}"` };
      saveGrade(applyLookGrade(grade, p));
      return { ok: true, look: p.name };
    },
    adjust: (args) => {
      const { grade: next, applied } = adjustGrade(grade, args);
      if (!applied) return { ok: false, error: `unknown control "${args.control}"` };
      saveGrade(next);
      return { ok: true, ...applied };
    },
    auto_enhance: ({ mode } = {}) => {
      if (!origStats) return { ok: false, error: 'preview still rendering' };
      saveGrade(autoGrade(origStats, grade, ['light', 'color', 'all'].includes(mode) ? mode : 'all'));
      return { ok: true, mode: mode || 'all' };
    },
    reset_grade: () => { saveGrade(clone(RAW_GRADE)); return { ok: true }; },
    add_mask: ({ target }) => {
      const idm = `m${Date.now().toString(36)}`;
      const m = target === 'sky'
        ? { id: idm, type: 'linear', ai: 'sky', cx: 0.5, cy: 0.45, rx: 0.3, ry: 0.3, angle: -90, feather: 0.4, invert: false, amount: 1, range: { enabled: true, lo: 0.45, hi: 1, feather: 0.18 }, adj: { ...DEFAULT_MASK_ADJ, exposure: -0.4, saturation: 8 } }
        : { id: idm, type: 'radial', ai: 'subject', cx: 0.5, cy: 0.55, rx: 0.32, ry: 0.34, feather: 0.5, invert: false, amount: 1, range: { ...DEFAULT_MASK_RANGE }, adj: { ...DEFAULT_MASK_ADJ, exposure: 0.25 } };
      saveGrade({ ...grade, masks: [...(grade.masks || []), m] });
      return { ok: true, target: target || 'subject' };
    },
    generate_variations: ({ direction } = {}) => {
      if (!assistantRef.current) return { ok: false, error: 'assistant not ready' };
      assistantRef.current.runVariations(direction);
      return { ok: true };
    },
  });
  useAgentContext(
    { pageName: 'Movie color stage', movieName: movie?.title || movie?.name, clips: clips.map((c, i) => c.label || `Clip ${i + 1}`), presets: presetNames(presets || []) },
    [movie?.title, movie?.name, clips.length, (presets || []).length],
  );

  // Subject choice + corrected center path for the ACTIVE clip.
  const saveReframe = (next) => {
    commitReframe({ ...rfRef.current, subjectTrackId: next.subjectTrackId, waypoints: next.waypoints });
  };

  // Crop center + zoom for the ACTIVE clip (instant preview, debounced persist).
  // Subject mode shares the zoom (crop size) but keeps its tracked center.
  const saveManual = (next) => {
    commitReframe({ ...rfRef.current, manualCrop: next });
  };

  // ---- Zoom keyframes (per-clip, master time) -------------------------------
  // Animate the crop SIZE over a clip: set the zoom at a few playhead times and
  // the export pushes in / pulls out between them (smoothed like the pan).
  const curMasterT = () => Math.round((videoRef.current?.currentTime || 0) * 100) / 100;
  const saveZoomKeys = (keys) =>
    commitReframe({ ...rfRef.current, zoomKeys: keys.slice().sort((a, b) => a.t - b.t) });
  // Effective zoom at a master time: keyframed if any keys, else the base scale.
  const effScaleAt = (tt) => scaleAt(rfRef.current.zoomKeys, rfRef.current.manualCrop.scale, tt);
  const setZoomKeyAt = (tt, scale) => {
    const ct = Math.round(tt * 100) / 100;
    const s = Math.max(0.25, Math.min(1, scale));
    const next = (rfRef.current.zoomKeys || []).filter((k) => Math.abs(k.t - ct) > 0.15);
    next.push({ t: ct, scale: s });
    saveZoomKeys(next);
  };
  const removeZoomKey = (kt) =>
    saveZoomKeys((rfRef.current.zoomKeys || []).filter((k) => k.t !== kt));
  const addZoomKeyHere = () => setZoomKeyAt(curMasterT(), effScaleAt(curMasterT()));
  const clearZoomKeys = () => commitReframe({ ...rfRef.current, zoomKeys: [] });
  // The zoom slider edits the keyframe under the playhead when keyframing is on,
  // otherwise the clip's single base zoom.
  const onZoomSlider = (val) => {
    if ((rfRef.current.zoomKeys || []).length) setZoomKeyAt(curMasterT(), val);
    else saveManual({ ...manualRef.current, scale: val });
  };
  const chooseCropMode = (m) => {
    if (m === 'manual') setLayers((p) => ({ ...p, reframe: true }));
    commitReframe({ ...rfRef.current, cropMode: m });
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    // Refuse to start a STALE master (its timing lies about the current cut) —
    // pausing is always allowed.
    if (v.paused && masterStale) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const pauseCtl = useCallback(() => {
    const v = videoRef.current;
    if (v) v.pause();
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (onPlayingChange) onPlayingChange(playing);
  }, [playing, onPlayingChange]);
  useEffect(() => {
    if (!ctlRef) return undefined;
    ctlRef.current = { toggle: togglePlay, pause: pauseCtl };
    return () => { if (ctlRef) ctlRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctlRef, pauseCtl]);

  // The composite shown on the right pane, layer by layer.
  const compositeGrade = useMemo(() => composeGrade(grade, layers), [grade, layers]);
  const live = useMemo(() => activeLayers(grade), [grade]);

  // The active clip's own saved look, applied BENEATH the movie grade on a raw
  // master so the right pane = clip look + movie look (the renderer's order), and
  // the left "Original" pane shows the true raw frame. Updates as the playhead
  // crosses clips. On legacy baked masters this stays null (look is in the file).
  const clipLook = useMemo(() => {
    if (!masterRaw) return null;
    const cg = activeClip && activeClip.useGrade !== false ? activeClip.grade : null;
    if (!cg || isIdentityGrade(cg)) return null;
    return composeGrade(cg, layers);
  }, [masterRaw, activeClip, layers]);

  // Per-clip crop windows in MASTER time for the social export — one segment per
  // clip tile, each carrying that clip's mode / zoom / center (+ subject path).
  // The active clip uses its live working copy so the export matches the preview.
  const cropSegments = useMemo(() => {
    const r3 = (x) => Math.round(x * 1000) / 1000;
    return segs.map((s) => {
      const eff = s.clip.id === activeClipId ? rf : clipReframe(s.clip);
      // Dense subject path, same blend as the publish bake: the locked track's
      // raw samples anchored to the edited keyframes.
      const segTrack = eff.cropMode === 'subject' && eff.subjectTrackId
        ? (clipReframes[s.clip.id]?.tracks || []).find((tr) => tr.id === eff.subjectTrackId)
        : null;
      const segWps = eff.cropMode === 'subject'
        ? (segTrack ? anchoredDenseWaypoints(segTrack.samples || [], eff.waypoints || []) : (eff.waypoints || []))
        : [];
      return {
        t0: r3(s.tileStart),
        t1: r3(s.tileEnd),
        // Lets the social worker bake THIS clip's saved color/light look onto its
        // own tile when cropping a raw master (matches the live preview below).
        clipId: s.clip.id,
        mode: eff.cropMode,
        scale: eff.manualCrop.scale,
        cx: eff.manualCrop.cx,
        cy: eff.manualCrop.cy,
        waypoints: segWps,
        // Per-clip zoom animation (master time); empty = constant size.
        zoomKeys: (eff.zoomKeys || []).map((k) => ({ t: r3(k.t), scale: r3(k.scale) })),
        // Pan/zoom smoothing (EMA alpha) so the bake glides like the preview.
        smoothing: Math.min(1, Math.max(0.05, Number(eff.smoothing) || 0.35)),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segs, activeClipId, rf, clipReframes]);
  // The crop window is always previewable: manual crops the master directly, and
  // subject mode shows the configured ratio+size centered (frame center until an
  // analysis provides a subject path to pan along). This lets the user dial in
  // crop ratio + size FIRST, then analyze to keep the subject centered in it.
  const cropReady = reReady || cropMode === 'manual' || cropMode === 'subject';
  const cropOn = layers.reframe && cropReady;
  // On the Reframe tab the RIGHT pane always shows the live crop/zoom result —
  // not an overlay box — so the user sees exactly what the reframe produces (at
  // the chosen ratio's true aspect, never squished). On the Color tab it still
  // respects the "Reframe / Crop" layer toggle.
  const rightCropOn = (editor === 'reframe' || layers.reframe) && cropReady;

  // Per-frame crop window (normalized) for the right pane. Looks up the clip
  // under the playhead and uses ITS reframe (the active clip reads the live
  // working copy so dragging is instant; others read their stored config). So as
  // playback crosses clips, the crop window changes per clip.
  const getCrop = useCallback((v) => {
    const { srcW: sw, srcH: sh } = srcRef.current;
    const def = ASPECTS.find((a) => a.id === aspectRef.current) || ASPECTS[0];
    const base = cropRectFor(def.w, def.h, sw, sh);
    const tt = (v && v.currentTime) || 0;
    const ci = clipIndexAtMaster(segsRef.current, tt);
    const clip = ci >= 0 ? segsRef.current[ci].clip : null;
    const r = clip && clip.id === activeIdRef.current ? rfRef.current : clipReframe(clip);
    const ss = Math.max(0.25, Math.min(1, scaleAt(r.zoomKeys, r.manualCrop.scale, tt)));
    const cw = Math.min(sw, Math.round(base.w * ss));
    const ch = Math.min(sh, Math.round(base.h * ss));
    let cx;
    let cy;
    if (r.cropMode === 'manual') {
      cx = r.manualCrop.cx;
      cy = r.manualCrop.cy;
    } else {
      // Active clip uses the pre-smoothed path (matches export glide); others
      // fall back to the linear interpolation of their stored waypoints.
      const sp = clip && clip.id === activeIdRef.current ? smoothPathRef.current : null;
      const c = sp && sp.length ? centerAt(sp, tt) : centerAt(r.waypoints, tt);
      cx = c.cx;
      cy = c.cy;
    }
    const xy = waypointToXY(cx, cy, cw, ch, sw, sh);
    return { x: xy.x / sw, y: xy.y / sh, w: cw / sw, h: ch / sh };
  }, []);

  // Derive an editable keyframe path from a track's detections over the active
  // clip's window, thinned to the current density (so you get a handful of
  // draggable keyframes instead of hundreds of raw detections).
  const buildSubjectWaypoints = (track, density) => {
    const t0 = activeSeg ? activeSeg.tileStart : 0;
    const t1 = activeSeg ? activeSeg.tileEnd : Infinity;
    let slice = (track.samples || []).filter((p) => p.t >= t0 - 0.5 && p.t <= t1 + 0.5);
    // Belt-and-suspenders: skip a low-confidence "warm-up" lead-in (the surfer is
    // small/lost in spray at takeoff) so the path doesn't open panned off-subject.
    if (slice.length >= 6 && slice.some((p) => p.score != null)) {
      const scores = slice.map((p) => p.score || 0).slice().sort((a, b) => a - b);
      const medScore = scores[Math.floor(scores.length / 2)] || 0;
      const floor = Math.max(0.35, medScore * 0.7);
      const maxTrim = Math.floor(slice.length * 0.4);
      let start = 0;
      while (start < maxTrim && (slice[start].score || 0) < floor) start += 1;
      if (start > 0) slice = slice.slice(start);
    }
    const wp = thinPath(slice.map((p) => ({ t: p.t, cx: p.cx, cy: p.cy })), density);
    return wp.length ? wp : [{ t: t0, cx: 0.5, cy: 0.5 }];
  };

  // Lock the PRIMARY subject for the active clip (drives the crop center). Pull
  // just this clip's slice of the track's path (master time), thinned to the
  // keyframe density, so it stays centered for THIS clip only; demote it from the
  // secondary list if it was there.
  const lockSubject = (track) => {
    commitReframe({
      ...rfRef.current,
      cropMode: 'subject',
      subjectTrackId: track.id,
      secondaryTrackIds: (rfRef.current.secondaryTrackIds || []).filter((x) => x !== track.id),
      waypoints: buildSubjectWaypoints(track, kfDensity),
    });
  };

  // Change the keyframe density. If a subject is locked, re-derive its path at
  // the new spacing (discarding manual tweaks — it's a deliberate re-thin).
  const setDensity = (val) => {
    const v = Math.max(0.2, Math.min(3, val));
    setKfDensity(v);
    try { localStorage.setItem('cs_kf_density', String(v)); } catch { /* noop */ }
    const tr = tracks.find((x) => x.id === rfRef.current.subjectTrackId);
    if (tr) {
      commitReframe({ ...rfRef.current, cropMode: 'subject', waypoints: buildSubjectWaypoints(tr, v) });
    }
  };

  // Set the pan/zoom smoothing from the 0..100% slider (100% = very smooth).
  const setSmoothPct = (pctVal) => {
    const pctN = Math.max(0, Math.min(100, pctVal));
    const alpha = Math.round((1 - (pctN / 100) * 0.95) * 1000) / 1000;
    commitReframe({ ...rfRef.current, smoothing: alpha });
  };

  // Toggle a SECONDARY subject for the active clip — tracked + highlighted for
  // reference (e.g. a second surfer), but it doesn't move the crop. The primary
  // is never also secondary.
  const toggleSecondary = (trackId) => {
    if (trackId === rfRef.current.subjectTrackId) return;
    const cur = rfRef.current.secondaryTrackIds || [];
    const next = cur.includes(trackId) ? cur.filter((x) => x !== trackId) : [...cur, trackId];
    commitReframe({ ...rfRef.current, secondaryTrackIds: next });
  };

  // Analyze (or re-analyze) ONLY the active clip's tile window.
  const analyzeActiveClip = () => {
    if (!activeClipId || !activeSeg) return;
    requestClipReframe(id, activeClipId, { t0: activeSeg.tileStart, t1: activeSeg.tileEnd }).catch(() => {});
  };

  const onFrameClick = (e) => {
    if (editor !== 'reframe' || cropMode === 'manual') return;
    const rect = wrapRef.current.getBoundingClientRect();
    const cx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const cy = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const ct = Math.round((videoRef.current?.currentTime || 0) * 100) / 100;
    const next = (reEdit.waypoints || []).filter((w) => Math.abs(w.t - ct) > 0.15);
    next.push({ t: ct, cx, cy });
    next.sort((a, b) => a.t - b.t);
    saveReframe({ subjectTrackId: reEdit.subjectTrackId, waypoints: next });
  };

  const removeWaypoint = (wt) =>
    saveReframe({ subjectTrackId: reEdit.subjectTrackId, waypoints: reEdit.waypoints.filter((w) => w.t !== wt) });

  // Commit a full waypoint list from the Subject-path lane (add / drag / group
  // move / delete all route through here). Rounds, clamps, sorts, and merges any
  // keyframes that landed on the same moment (last one wins), then persists.
  const commitWaypoints = (nextWps) => {
    const r3 = (x) => Math.round(x * 1000) / 1000;
    const cl = (x) => Math.max(0, Math.min(1, x));
    const mapped = (nextWps || [])
      .map((w) => ({ t: r3(w.t), cx: r3(cl(w.cx == null ? 0.5 : w.cx)), cy: r3(cl(w.cy == null ? 0.5 : w.cy)) }))
      .sort((a, b) => a.t - b.t);
    const cleaned = [];
    for (const w of mapped) {
      if (cleaned.length && Math.abs(cleaned[cleaned.length - 1].t - w.t) <= 0.02) cleaned[cleaned.length - 1] = w;
      else cleaned.push(w);
    }
    commitReframe({ ...rfRef.current, cropMode: 'subject', subjectTrackId: rfRef.current.subjectTrackId, waypoints: cleaned });
  };

  // Seek the master (and resync the active clip) from the frame strip / lanes.
  const seekMaster = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = Math.max(0, sec); } catch { /* noop */ }
    setT(sec);
    const i = clipIndexAtMaster(segsRef.current, sec);
    if (i >= 0) setSegIndex((prev) => (prev === i ? prev : i));
  };

  // Re-tag the best subject for THIS clip from a clicked box in the strip.
  const lockSubjectById = (trackId) => {
    const tr = tracks.find((x) => x.id === trackId);
    if (tr) lockSubject(tr);
  };

  // Drop / replace the center for THIS clip at a specific master time (from the
  // frame strip). Keeps subject mode and merges into the per-clip center path.
  const setCenterAt = (masterT, cx, cy) => {
    const ct = Math.round(masterT * 100) / 100;
    const next = (rfRef.current.waypoints || []).filter((w) => Math.abs(w.t - ct) > 0.15);
    next.push({ t: ct, cx, cy });
    next.sort((a, b) => a.t - b.t);
    commitReframe({ ...rfRef.current, cropMode: 'subject', waypoints: next });
  };

  // Manual crop: drag the crop box to set its center.
  const manualDragRef = useRef(false);
  const manualFromEvent = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const cy = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    saveManual({ ...manualRef.current, cx, cy });
  };
  const onManualMove = useCallback((e) => {
    if (manualDragRef.current) manualFromEvent(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onManualUp = useCallback(() => {
    manualDragRef.current = false;
    window.removeEventListener('pointermove', onManualMove);
    window.removeEventListener('pointerup', onManualUp);
  }, [onManualMove]);
  const onManualDown = (e) => {
    e.stopPropagation();
    manualDragRef.current = true;
    manualFromEvent(e);
    window.addEventListener('pointermove', onManualMove);
    window.addEventListener('pointerup', onManualUp);
  };

  // Drag a corner to resize the manual crop. The crop stays locked to the
  // selected aspect (social export needs a fixed ratio), so a corner maps to the
  // zoom `scale` — keeping the box centered where it is.
  const onManualCornerDown = (e, baseFit) => {
    e.stopPropagation();
    const wrap = wrapRef.current;
    if (!wrap || !baseFit?.w) return;
    const move = (ev) => {
      const r = wrap.getBoundingClientRect();
      const px = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
      const py = Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height));
      const m = manualRef.current;
      const sx = (2 * Math.abs(px - m.cx) * srcW) / baseFit.w;
      const sy = (2 * Math.abs(py - m.cy) * srcH) / baseFit.h;
      const scale = Math.max(0.25, Math.min(1, Math.max(sx, sy)));
      saveManual({ ...m, scale });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  if (!movie) return null;

  const hasClips = clips.length > 0;
  const composing = master.status === 'composing' || master.status === 'queued';
  const masterPending = !masterUrl;

  const aspectDef = ASPECTS.find((a) => a.id === aspect) || ASPECTS[0];
  const dur = master.durationSec || movieReframe.durationSec || 0;
  const center = centerAt(reEdit.waypoints, t);
  const ov = (() => {
    const base = cropRectFor(aspectDef.w, aspectDef.h, srcW, srcH);
    // Both modes honor the crop-size zoom (keyframed if zoom keys are set); subject
    // mode pans the window along the tracked center, manual uses a fixed center.
    const ss = scaleAt(zoomKeys, manual.scale, t);
    let cw = Math.min(srcW, base.w * ss);
    let ch = Math.min(srcH, base.h * ss);
    let cx = center.cx;
    let cy = center.cy;
    if (cropMode === 'manual') {
      cx = manual.cx;
      cy = manual.cy;
    }
    const xy = waypointToXY(cx, cy, cw, ch, srcW, srcH);
    return { x: (xy.x / srcW) * 1000, y: (xy.y / srcH) * 1000, w: (cw / srcW) * 1000, h: (ch / srcH) * 1000 };
  })();

  // Shared zoom control (used by both Manual and Subject panels): a slider that
  // edits the clip's zoom — or, once keyframes exist, the zoom at the playhead —
  // plus add/clear keys and a keyframe lane so the crop can push in/out per clip.
  const renderZoomControl = () => {
    const eff = scaleAt(zoomKeys, manual.scale, t);
    const keyed = zoomKeys.length > 0;
    // Window the zoom track to the ACTIVE CLIP (master time) so keys spread
    // across the clip instead of being squished into a sliver of the full movie
    // — that's what makes a within-clip zoom ramp visible + editable.
    const zWinStart = activeSeg ? activeSeg.tileStart : 0;
    const zWinEnd = activeSeg ? activeSeg.tileEnd : (dur > 0 ? dur : 1);
    const zWinDur = zWinEnd - zWinStart || 1;
    const zXof = (tt) => Math.max(0, Math.min(100, ((tt - zWinStart) / zWinDur) * 100));
    const zClipKeys = zoomKeys.filter((k) => k.t >= zWinStart - 0.05 && k.t <= zWinEnd + 0.05);
    const addZoomKeyFromTrack = (e) => {
      if (e.target.closest && e.target.closest('.cs-zoom-marker')) return; // not on a key
      const rect = e.currentTarget.getBoundingClientRect();
      const f = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const tt = zWinStart + f * zWinDur;
      setZoomKeyAt(tt, effScaleAt(tt));
      seekMaster(Math.round(tt * 100) / 100);
    };
    return (
      <div className="cs-zoomctl">
        <label className="cs-zoomctl-label">
          <span>
            Zoom {Math.round((1 / eff) * 100) / 100}×{' '}
            <span style={{ opacity: 0.7 }}>(crop {Math.round(eff * 100)}% of frame){keyed ? ' · at playhead' : ''}</span>
          </span>
          <input
            className="cs-slider"
            type="range"
            min="0.25"
            max="1"
            step="0.01"
            value={eff}
            onChange={(e) => onZoomSlider(Number(e.target.value))}
          />
        </label>
        <div className="cs-zoomctl-row">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={addZoomKeyHere} title="Pin the current zoom at the playhead — the crop eases to/from it over the clip">◆ Add zoom key</button>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onZoomSlider(1)}>Widest (100%)</button>
          {keyed && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={clearZoomKeys}>Clear keys ({zoomKeys.length})</button>}
          {!layers.reframe && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setLayers((p) => ({ ...p, reframe: true }))}>Turn on crop preview</button>
          )}
        </div>
        {keyed ? (
          <>
            <div className="cs-help" style={{ margin: '2px 0 4px' }}>
              Zoom is <b>keyframed for this clip</b> — the crop eases between keys across the clip. Click the lane to add a key, drag the slider to set the zoom at the playhead, shift-/right-click a key to delete. Need 2+ keys at different times to ramp.
            </div>
            <div
              className="cs-wp-track cs-zoomtrack"
              onClick={addZoomKeyFromTrack}
              title="Click to add a zoom key at that point in the clip"
            >
              {zClipKeys.map((k) => (
                <button
                  key={k.t}
                  className="cs-wp-marker cs-zoom-marker"
                  style={{ left: `${zXof(k.t)}%` }}
                  title={`${fmtTime(k.t)} · ${Math.round((1 / k.scale) * 100) / 100}× — click to seek, shift-/right-click to delete`}
                  onClick={(e) => { e.stopPropagation(); if (e.shiftKey) removeZoomKey(k.t); else seekMaster(k.t); }}
                  onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); removeZoomKey(k.t); }}
                />
              ))}
              <div className="cs-wp-playhead" style={{ left: `${zXof(t)}%` }} />
            </div>
            <div className="cs-zoomtrack-ticks">
              <span>{zWinStart.toFixed(1)}s</span>
              <span>{zWinEnd.toFixed(1)}s</span>
            </div>
          </>
        ) : (
          <div className="cs-help" style={{ margin: '2px 0 4px' }}>
            One zoom for the whole clip. To push in or pull out partway, scrub to a moment and <b>◆ Add zoom key</b> (then add another later at a different zoom to ramp).
          </div>
        )}
      </div>
    );
  };

  // Scale the preview to fit the stage (height-bounded) while preserving the
  // source aspect, so a short Reframe stage shrinks the image instead of letting
  // overflow:hidden crop it wider. width/height:auto + max-* keeps the element
  // box equal to the displayed image — which the tracking overlay and the
  // click-to-place math both depend on (wrapRef rect == image box).
  const previewMaxH = stageH > 0
    ? `${Math.max(140, stageH - (viewMode === 'single' ? 8 : 48))}px`
    : '70vh';
  const fitStyle = { maxWidth: '100%', maxHeight: previewMaxH, width: 'auto', height: 'auto', display: 'block' };
  const leftStyle = fitStyle;
  const rightStyle = { ...fitStyle, margin: '0 auto' };

  const reframeOverlayOn = editor === 'reframe' && cropReady;

  const layerRows = [
    { key: 'light', label: 'Light', hint: 'Exposure, contrast, tone', live: live.light },
    { key: 'color', label: 'Color', hint: 'WB, vibrance, color mixer, grading', live: live.color },
    { key: 'effects', label: 'Effects', hint: 'Clarity, vignette, grain, sharpen', live: live.effects },
    { key: 'reframe', label: 'Reframe / Crop', hint: 'Subject-centered social crop', live: reReady },
  ];

  const paneW = (big) => (big ? 1920 : SIDE_W);

  // Left = original (with reframe tracking overlay). `big` widens it for single view.
  const leftPane = (big) => (
    <div className="cs-studio-leftwrap" ref={wrapRef} onClick={onFrameClick}>
      {videoEl ? (
        <GradedCanvas source={videoEl} grade={RAW_GRADE} onStats={setOrigStats} onError={setErr} maxWidth={paneW(big)} style={leftStyle} className="cs-cap-master" />
      ) : (
        <div className="cs-studio-ph">
          <span className="cs-spinner" />
          <strong>Original</strong>
          <small>fills in when the master is ready</small>
        </div>
      )}
      {reframeOverlayOn && videoEl && (
        <svg className="cs-studio-overlay" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          {cropMode === 'subject' && showTrack && tracks.map((tr, ti) => {
            const samples = tr.samples || [];
            if (samples.length < 1) return null;
            const isSub = tr.id === reEdit.subjectTrackId;
            const isSec = (reEdit.secondaryTrackIds || []).includes(tr.id);
            const emph = isSub || isSec || tr.id === hoverTrackId;
            const col = trackColor(ti);
            const pts = samples.map((s) => `${s.cx * 1000},${s.cy * 1000}`).join(' ');
            return (
              <g key={`path-${tr.id}`} style={{ opacity: emph ? 1 : 0.45 }}>
                {samples.length > 1 && (
                  <polyline points={pts} className="cs-rf-path" style={{ stroke: col, strokeWidth: isSub ? 2.5 : 1.5 }} />
                )}
                {samples.map((s, i) => (
                  <circle key={i} cx={s.cx * 1000} cy={s.cy * 1000} r={emph ? 6 : 4} className="cs-rf-node" style={{ fill: col }} />
                ))}
              </g>
            );
          })}
          {cropMode === 'subject' && tracks.map((tr, ti) => {
            const b = interpTrack(tr, t);
            if (!b) return null;
            const isSub = tr.id === reEdit.subjectTrackId;
            const isSec = (reEdit.secondaryTrackIds || []).includes(tr.id);
            const emph = isSub || isSec || tr.id === hoverTrackId;
            const col = trackColor(ti);
            return (
              <rect
                key={tr.id}
                x={(b.cx - b.w / 2) * 1000}
                y={(b.cy - b.h / 2) * 1000}
                width={b.w * 1000}
                height={b.h * 1000}
                className="cs-rf-box"
                style={{ stroke: col, strokeWidth: isSub ? 3 : 2, fill: emph ? `${col}22` : `${col}11`, opacity: emph ? 1 : 0.55 }}
                onClick={(e) => { e.stopPropagation(); lockSubject(tr); }}
                onMouseEnter={() => setHoverTrackId(tr.id)}
                onMouseLeave={() => setHoverTrackId((h) => (h === tr.id ? null : h))}
              />
            );
          })}
          {/* Manual mode keeps a draggable crop box on the original so you can
              position it; subject mode shows only the tracking path here and the
              cropped result lives on the right pane (no overlay box). */}
          {cropMode === 'manual' && (
            <rect
              x={ov.x}
              y={ov.y}
              width={ov.w}
              height={ov.h}
              className="cs-rf-crop cs-rf-crop-drag"
              onPointerDown={onManualDown}
            />
          )}
          {cropMode === 'manual' && (() => {
            const baseFit = cropRectFor(aspectDef.w, aspectDef.h, srcW, srcH);
            // Keep grips visually square despite preserveAspectRatio="none".
            const gh = 30;
            const gw = 30 * ((srcH || 9) / (srcW || 16));
            const corners = [
              [ov.x, ov.y], [ov.x + ov.w, ov.y],
              [ov.x, ov.y + ov.h], [ov.x + ov.w, ov.y + ov.h],
            ];
            return corners.map(([hx, hy], i) => (
              <rect
                key={`grip-${i}`}
                x={hx - gw / 2}
                y={hy - gh / 2}
                width={gw}
                height={gh}
                className="cs-rf-grip"
                onPointerDown={(e) => onManualCornerDown(e, baseFit)}
              />
            ));
          })()}
        </svg>
      )}
    </div>
  );

  // Right = live composite of the enabled grade + reframe layers.
  const rightPane = (big) =>
    videoEl ? (
      <GradedCanvas
        source={videoEl}
        grade={compositeGrade}
        baseGrade={clipLook || undefined}
        getCrop={rightCropOn ? getCrop : undefined}
        onStats={setStats}
        onError={setErr}
        maxWidth={paneW(big)}
        style={rightStyle}
        className="cs-cap-graded"
      />
    ) : (
      <div className="cs-studio-ph">
        <span className="cs-spinner" />
        <strong>Composite</strong>
        <small>your enabled layers render here</small>
      </div>
    );

  return (
    <>
      {!hasClips ? (
        <div className="cs-section"><div className="cs-empty">Add clips to this movie first — the studio needs a composition to grade and reframe.</div></div>
      ) : (
        <div className={`cs-studio${sideHidden ? ' cs-side-off' : ''}`}>
          <div className="cs-studio-main">
            <video
              ref={videoRef}
              src={masterUrl || undefined}
              crossOrigin="anonymous"
              muted
              loop
              playsInline
              style={{ display: 'none' }}
              onError={() => masterUrl && setErr('Could not load preview master.')}
            />
            {masterPending && (
              <div className={`cs-master-banner ${master.status === 'error' ? 'err' : ''}`}>
                {master.status === 'error' ? (
                  <>
                    <span>Preview master failed: {master.message || 'unknown error'}. Your edits are still saved.</span>
                    <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={() => requestMaster(id, masterCutSig(clips))}>Retry build</button>
                  </>
                ) : composing ? (
                  <>
                    <span className="cs-spinner" />
                    <span style={{ flex: '1 1 auto' }}>
                      <b>{jobEtaLabel(master, { verb: 'Building preview', estSec: masterEtaSec, nowMs: now })}</b> —
                      subject tracking and social crops use this flat master. It fills in automatically when ready.
                    </span>
                    <div className="cs-progress cs-master-prog"><div style={{ width: `${master.progress || 4}%` }} /></div>
                  </>
                ) : (
                  <>
                    <span style={{ flex: '1 1 auto' }}>
                      Reframe works on a flat composite of your cut. Build it once to track subjects and crop
                      for social — it only renders when you ask (~{fmtDuration(masterEtaSec)}).
                    </span>
                    <button
                      className="cs-btn cs-btn-sm cs-btn-primary"
                      onClick={() => requestMaster(id, masterCutSig(clips))}
                      title={`Compose the flat preview master (~${fmtDuration(masterEtaSec)})`}
                    >
                      Build preview
                    </button>
                  </>
                )}
              </div>
            )}
            {!masterPending && (masterStale || !masterRaw) && (
              <div className="cs-master-banner">
                <span style={{ flex: '1 1 auto' }}>
                  {!masterRaw ? (
                    <>
                      This preview predates <b>live color</b> — <b>rebuild</b> so the crop preview shows each
                      clip’s current color/light look (it’s rendered live now, matching the Color tab and the
                      published output), plus the latest speeds, trims and dissolves.
                    </>
                  ) : (
                    <>
                      This preview was built from an earlier cut, so clip <b>playback speed / timing</b> here
                      doesn’t match Assemble (clips may look sped up). Rebuild to bake in the current per-clip
                      speeds, trims and dissolves — then tracking and crops line up too.
                    </>
                  )}
                </span>
                <button
                  className="cs-btn cs-btn-sm cs-btn-primary"
                  onClick={() => requestMaster(id, masterCutSig(clips))}
                  title={`Rebuild the flat preview master (~${fmtDuration(masterEtaSec)})`}
                >
                  Rebuild preview
                </button>
              </div>
            )}
            <div ref={stageRef} className={`cs-studio-stage${viewMode === 'single' ? ' single' : ''}`}>
              {err ? (
                <div className="cs-empty">{err}</div>
              ) : viewMode === 'single' ? (
                <div className="cs-studio-single">
                  <div className="cs-split-label" style={{ background: 'var(--surf)' }}>
                    {editor === 'reframe'
                      ? (cropMode === 'manual'
                        ? `Drag crop · ${aspectDef.label}`
                        : `Reframed · ${aspectDef.label}`)
                      : cropOn
                      ? `Composite · ${aspectDef.label}`
                      : 'Composite'}
                  </div>
                  {/* On Reframe, single view shows the live reframed result, except
                      in manual mode where the draggable crop box lives on the left. */}
                  {editor === 'reframe'
                    ? (cropMode === 'manual' ? leftPane(true) : rightPane(true))
                    : rightPane(true)}
                </div>
              ) : (
                <SplitPreview
                  panes={[
                    { label: 'Original', color: '#888' },
                    { label: rightCropOn ? `Reframed · ${aspectDef.label}` : 'Composite', color: 'var(--surf)' },
                  ]}
                >
                  {leftPane(false)}
                  {rightPane(false)}
                </SplitPreview>
              )}
            </div>
            <div className="cs-studio-bar">
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={togglePlay} disabled={!videoEl}>{playing ? '❚❚ Pause' : '► Play'}</button>
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => setViewMode((v) => (v === 'split' ? 'single' : 'split'))}
                title={viewMode === 'split' ? 'Expand to a single large view' : 'Back to side-by-side'}
              >
                {viewMode === 'split' ? '⤢ Single view' : '⊞ Split view'}
              </button>
              <div className="cs-layerchips">
                {layerRows.map((r) => {
                  const disabled = r.key === 'reframe' && !cropReady;
                  return (
                    <button
                      key={r.key}
                      className={`cs-layerchip ${layers[r.key] ? 'on' : ''} ${disabled ? 'cs-layerchip-dim' : ''}`}
                      title={disabled ? 'Analyze the subject first (Reframe panel)' : `Toggle ${r.label} on the right view`}
                      disabled={disabled}
                      onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
                    >
                      <span className="cs-layerchip-box">{layers[r.key] ? '✓' : ''}</span>{r.label}
                    </button>
                  );
                })}
              </div>
              <span className="cs-help">Left = original · right builds up your enabled layers live.</span>
            </div>
            <div className="cs-rf-lanes">
            {editor === 'reframe' && cropMode === 'subject' && !showPath && (
              <div className="cs-pathlane-wrap">
                <div className="cs-pathlane-collapsed">
                  <button
                    type="button"
                    className="cs-mini-toggle"
                    title="Show the subject path panel"
                    aria-label="Show subject path panel"
                    onClick={() => setShowPath(true)}
                  >
                    ＋
                  </button>
                  <strong>Subject path</strong>
                  <span className="cs-help">hidden — click ＋ to show the tracking lane</span>
                </div>
              </div>
            )}
            {editor === 'reframe' && cropMode === 'subject' && showPath && (() => {
              const subIdx = Math.max(0, tracks.findIndex((tr) => tr.id === reEdit.subjectTrackId));
              const subTrack = tracks[subIdx] || tracks[0] || null;
              return (
                <div className="cs-pathlane-wrap">
                  <div className="cs-pathlane-head">
                    <button
                      type="button"
                      className="cs-mini-toggle"
                      title="Hide the subject path panel (bigger preview)"
                      aria-label="Hide subject path panel"
                      onClick={() => setShowPath(false)}
                    >
                      －
                    </button>
                    <strong>Subject path</strong>
                    <span className="cs-help">
                      Drag diamonds to adjust · double-click to add · box-select to multi · alt-click / right-click / Delete to remove
                    </span>
                    {reReady && (
                      <div className="cs-segment cs-segment-sm" style={{ marginLeft: 'auto' }}>
                        <button className={pathAxis === 'cx' ? 'on' : ''} onClick={() => setPathAxis('cx')}>Horizontal</button>
                        <button className={pathAxis === 'cy' ? 'on' : ''} onClick={() => setPathAxis('cy')}>Vertical</button>
                      </div>
                    )}
                  </div>
                  {reReady && (
                    <div className="cs-pathlane-tools">
                      <label className="cs-pl-ctl" title="Keyframe spacing when you lock a subject (★). Fewer keyframes are easier to hand-edit; more hug every detection.">
                        <span>Keyframes</span>
                        <input
                          type="range"
                          min="0.2"
                          max="2.5"
                          step="0.1"
                          value={3.2 - kfDensity}
                          onChange={(e) => setDensity(3.2 - Number(e.target.value))}
                        />
                        <span className="cs-pl-ctlval">{kfDensity <= 0.4 ? 'Dense' : kfDensity >= 1.5 ? 'Sparse' : 'Medium'}</span>
                      </label>
                      <label className="cs-pl-ctl" title="How smoothly the crop glides between keyframes (matches the bake). Higher = smoother camera move; lower = snappier/follows keyframes tightly.">
                        <span>Smoothing</span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={smoothPct}
                          onChange={(e) => setSmoothPct(Number(e.target.value))}
                        />
                        <span className="cs-pl-ctlval">{smoothPct}%</span>
                      </label>
                    </div>
                  )}
                  {reReady ? (
                    <SubjectPathLane
                      dur={dur}
                      t={t}
                      t0={activeSeg ? activeSeg.tileStart : 0}
                      t1={activeSeg ? activeSeg.tileEnd : 0}
                      track={subTrack}
                      waypoints={reEdit.waypoints}
                      axis={pathAxis}
                      color={trackColor(subIdx)}
                      smoothing={smoothAlpha}
                      onSeek={(sec) => { const v = videoRef.current; if (v) { try { v.currentTime = Math.max(0, sec); } catch { /* noop */ } setT(sec); } }}
                      onCommitWaypoints={commitWaypoints}
                    />
                  ) : (
                    <div className="cs-pathlane cs-pathlane-empty">
                      {reActive ? (
                        <>
                          <span className="cs-spinner" />
                          <span>Analyzing… tracking checkpoints and the smoothing line will appear here.</span>
                        </>
                      ) : (
                        <span>
                          Run <strong>“Analyze subject”</strong> to detect the surfer — the tracking checkpoints
                          (dots) and the smoothing line that infers the crop between them will plot here.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {editor === 'reframe' && cropMode === 'subject' && showFrames && masterUrl && activeSeg && (
              <div className="cs-pathlane-wrap">
                <div className="cs-pathlane-head">
                  <strong>Clip {segIndex + 1}/{clips.length} · frames + tracking</strong>
                  <span className="cs-help">
                    Fix mistracking for this clip — click a box to lock that subject, click elsewhere to set
                    the center, or jump to a frame and fine-tune in the big preview.
                  </span>
                </div>
                <FrameTrackStrip
                  url={masterUrl}
                  streamType={/\.m3u8(\?|$)/.test(masterUrl) ? 'hls' : undefined}
                  tileStart={activeSeg.tileStart}
                  tileEnd={activeSeg.tileEnd}
                  clipKey={activeClipId}
                  tracks={tracks}
                  subjectTrackId={reEdit.subjectTrackId}
                  waypoints={reEdit.waypoints}
                  trackColor={trackColor}
                  onSeek={seekMaster}
                  onLockSubject={lockSubjectById}
                  onSetCenter={setCenterAt}
                />
              </div>
            )}
            </div>
            {footer}
          </div>

          <div className="cs-studio-side">
            {/* Hidden via CSS (cs-side-off) rather than unmounted so tool state
                (active editor, expanded sections) survives the toggle. */}
            {onToggleSide && (
              <div className="cs-sidehead">
                <button
                  type="button"
                  className="cs-panelhide"
                  title="Hide this panel — restore it with the ＋ tools chip in the timeline toolbar"
                  aria-label="Hide reframe tools panel"
                  onClick={onToggleSide}
                >
                  −
                </button>
              </div>
            )}
            <div className="cs-layerlist">
              {layerRows.map((r) => {
                const editsHere = r.key === 'reframe' ? editor === 'reframe' : editor === 'grade';
                const disabled = r.key === 'reframe' && !cropReady;
                return (
                  <div key={r.key} className={`cs-layerrow ${editsHere ? 'sel' : ''}`}>
                    <button
                      className={`cs-toggle ${layers[r.key] ? 'on' : ''}`}
                      disabled={disabled}
                      title={`Show / hide ${r.label}`}
                      onClick={() => setLayers((p) => ({ ...p, [r.key]: !p[r.key] }))}
                    >
                      <span className="cs-toggle-knob" />
                    </button>
                    <button
                      className="cs-layername"
                      onClick={() => setEditor(r.key === 'reframe' ? 'reframe' : 'grade')}
                    >
                      <span className="cs-layertitle">{r.label}{r.live && <i className="cs-livedot" />}</span>
                      <span className="cs-layerhint">{r.hint}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            {editor === 'grade' ? (
              <>
                <div className="cs-autobar">
                  <div className="cs-autobar-row">
                    <button
                      className="cs-btn cs-btn-primary cs-btn-sm"
                      disabled={!origStats}
                      title={origStats ? 'Analyze this frame and apply a balanced auto correction' : 'Waiting for the preview to render'}
                      onClick={() => saveGrade(autoGrade(origStats, grade, 'all'))}
                    >
                      ✨ Auto enhance
                    </button>
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats} onClick={() => saveGrade(autoGrade(origStats, grade, 'light'))}>Auto light</button>
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!origStats} onClick={() => saveGrade(autoGrade(origStats, grade, 'color'))}>Auto color</button>
                    <button
                      className={`cs-btn cs-btn-sm ${grade.lut ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                      title={grade.lut ? `Creative LUT: ${grade.lut.name} — click to remove` : 'Import a creative .cube LUT (applied on top of the movie grade)'}
                      onClick={() => {
                        if (grade.lut) { const g = { ...grade }; delete g.lut; saveGrade(g); }
                        else lutInputRef.current?.click();
                      }}
                    >
                      {grade.lut ? `LUT · ${grade.lut.name} ✕` : '◩ LUT'}
                    </button>
                    <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => saveGrade(clone(RAW_GRADE))}>Reset</button>
                    <input
                      ref={lutInputRef}
                      type="file"
                      accept=".cube"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; e.target.value = '';
                        if (!f) return;
                        try {
                          const lut = lutFromCubeText(await f.text(), f.name.replace(/\.cube$/i, ''));
                          if (lut) saveGrade({ ...grade, lut });
                        } catch { /* ignore */ }
                      }}
                    />
                  </div>
                  <div className="cs-autobar-label">Styles</div>
                  <div className="cs-autobar-styles">
                    {PRESETS.map((p) => (
                      <button
                        key={p.id}
                        className={`cs-stylechip ${gradeSignature(grade) === gradeSignature(p.grade) ? 'on' : ''}`}
                        title={`Apply the ${p.name} look`}
                        onClick={() => saveGrade(stampPreset(p.grade, p))}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                  <div className="cs-help" style={{ marginTop: 6 }}>
                    Auto analyzes the current frame (exposure, contrast, white balance). Styles are
                    themed looks — start from one, then fine-tune below. All edits stay non-destructive.
                  </div>
                </div>
                <ColorAssistant
                  ref={assistantRef}
                  grade={grade}
                  baseGrade={clipLook || undefined}
                  source={videoEl}
                  onApply={saveGrade}
                  captureFrames={captureFrames}
                />
                <div>
                  <div className="cs-scopes-label">Scopes (final)</div>
                  <Scopes stats={stats} />
                </div>
                <GradePanel
                  grade={grade}
                  onChange={saveGrade}
                  kind="video"
                  presets={presets || []}
                  onSavePreset={(name, g) => addGradePreset({ name, grade: g, kind: 'video' })}
                  onDeletePreset={(pid) => deleteGradePreset(pid)}
                />
                <MaskPanel
                  masks={maskList}
                  activeId={activeMaskId}
                  onAdd={() => addMaskUi('radial')}
                  onAddLinear={() => addMaskUi('linear')}
                  onSelect={setActiveMaskId}
                  onDelete={deleteMaskUi}
                  onUpdate={patchMaskUi}
                  onUpdateAdj={patchMaskAdjUi}
                  onSnapSubject={snapMaskToSubject}
                  showPosition
                />
              </>
            ) : (
              <div className="cs-reframe-panel">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span className="cs-badge">Clip {segIndex + 1}/{clips.length}</span>
                  <strong style={{ fontSize: 13 }}>{activeClip?.label || `Clip ${segIndex + 1}`}</strong>
                </div>
                <div className="cs-help" style={{ marginBottom: 12 }}>
                  Crop is <b>per clip</b> — scrub the timeline to a clip, then set its crop here. The output
                  ratio is shared across all clips (one social file = one aspect); center, zoom and subject
                  are saved per clip.
                </div>
                <div className="cs-help" style={{ marginBottom: 6 }}>Crop source (this clip)</div>
                <div className="cs-segment" style={{ marginBottom: 14 }}>
                  <button className={cropMode === 'subject' ? 'on' : ''} onClick={() => chooseCropMode('subject')}>
                    Subject tracking
                  </button>
                  <button className={cropMode === 'manual' ? 'on' : ''} onClick={() => chooseCropMode('manual')}>
                    Manual crop
                  </button>
                </div>

                {cropMode === 'manual' && (
                  <div>
                    <p className="cs-section-sub" style={{ marginTop: 0 }}>
                      Drag inside the crop box to position it, drag a <b>corner</b> to resize, or use the
                      Zoom slider. The crop stays locked to the selected aspect, and the teal rectangle is
                      exactly what gets exported — no subject detection needed.
                    </p>

                    <div className="cs-help" style={{ marginBottom: 6 }}>Preview aspect</div>
                    <div className="cs-segment" style={{ marginBottom: 12 }}>
                      {ASPECTS.map((a) => (
                        <button key={a.id} className={aspect === a.id ? 'on' : ''} onClick={() => setAspect(a.id)}>{a.label}</button>
                      ))}
                    </div>

                    {renderZoomControl()}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => saveManual({ ...manual, cx: 0.5, cy: 0.5 })}>Center</button>
                    </div>
                    <div className="cs-help" style={{ marginBottom: 14 }}>
                      Center {Math.round(manual.cx * 100)}% , {Math.round(manual.cy * 100)}% for this clip. The crop
                      keeps each export format’s aspect; Zoom shrinks the window for a tighter framing.
                    </div>
                  </div>
                )}

                {cropMode === 'subject' && (<>
                <div style={{ marginBottom: 14 }}>
                  <div className="cs-help" style={{ marginBottom: 6 }}>Crop ratio</div>
                  <div className="cs-segment" style={{ marginBottom: 12 }}>
                    {ASPECTS.map((a) => (
                      <button key={a.id} className={aspect === a.id ? 'on' : ''} onClick={() => setAspect(a.id)}>{a.label}</button>
                    ))}
                  </div>
                  {renderZoomControl()}
                  <div className="cs-help" style={{ marginTop: 4 }}>
                    Set the output ratio and how tight to crop first — subject tracking then keeps the surfer
                    centered inside this window. 100% is the widest the ratio allows; add zoom keys to push in over the clip.
                  </div>
                </div>

                {!reReady && !reActive && (
                  <div>
                    <p className="cs-section-sub" style={{ marginTop: 0 }}>
                      Detect the surfer(s) in <b>clip {segIndex + 1}</b> and keep your pick centered while cropping
                      to each social format. Analysis runs per clip — analyze this one, then fine-tune its path.
                    </p>
                    {legacyTracks && (
                      <div className="cs-help" style={{ color: '#e0a414', marginBottom: 8 }}>
                        Showing this clip’s slice of the old movie-wide analysis — re-analyze this clip for its own tracks.
                      </div>
                    )}
                    {reStatus === 'error' && (
                      <div className="cs-empty" style={{ marginBottom: 8 }}>Analysis failed: {reframe.message || 'unknown error'}</div>
                    )}
                    <button
                      className="cs-btn cs-btn-primary"
                      onClick={analyzeActiveClip}
                      disabled={!activeClipId}
                      title={`Detect & track subjects in clip ${segIndex + 1} (~${fmtDuration(reframeEtaSec)})`}
                    >
                      {reStatus === 'error' ? 'Re-analyze this clip' : 'Analyze this clip'}
                    </button>
                    <span className="cs-help" style={{ marginLeft: 8 }}>~{fmtDuration(reframeEtaSec)}</span>
                  </div>
                )}

                {reActive && (
                  <div>
                    <div className="cs-help">{jobEtaLabel(reframe, { verb: `Analyzing clip ${segIndex + 1}`, estSec: reframeEtaSec, nowMs: now })} — you can keep editing.</div>
                    <div className="cs-progress" style={{ marginTop: 8 }}><div style={{ width: `${reframe.progress || 3}%` }} /></div>
                  </div>
                )}

                {reReady && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span className="cs-badge">clip {segIndex + 1} analyzed</span>
                      {clipReStale && <span className="cs-stalechip">⚠ Out of date — re-analyze</span>}
                      <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={analyzeActiveClip} title={`Re-analyze clip ${segIndex + 1} (~${fmtDuration(reframeEtaSec)})`}>↻ Re-analyze clip</button>
                      {!layers.reframe && (
                        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setLayers((p) => ({ ...p, reframe: true }))}>
                          Turn on crop preview
                        </button>
                      )}
                    </div>

                    <div className="cs-help" style={{ marginBottom: 6 }}>Overlays</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      <button
                        className={`cs-btn cs-btn-sm ${showTrack ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                        onClick={() => setShowTrack((s) => !s)}
                      >
                        {showTrack ? '◉ Boxes & path' : '○ Boxes & path'}
                      </button>
                      <button
                        className={`cs-btn cs-btn-sm ${showPath ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                        onClick={() => setShowPath((s) => !s)}
                      >
                        {showPath ? '◉ Path lane' : '○ Path lane'}
                      </button>
                      <button
                        className={`cs-btn cs-btn-sm ${showFrames ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                        onClick={() => setShowFrames((s) => !s)}
                        title="Show this clip’s frames with every detected subject, to re-tag the subject or fix the center"
                      >
                        {showFrames ? '◉ Frames + tracking' : '○ Frames + tracking'}
                      </button>
                    </div>
                    <div className="cs-help" style={{ marginBottom: 12 }}>
                      Each detected subject has its own color. Dots are the YOLO-detected frames; the line
                      is what drives the crop. The path lane under the preview shows how interim frames are
                      inferred between detections.
                    </div>

                    <div className="cs-help">Subjects in this clip — pick the <b>primary</b> (drives the crop); flag others as <b>secondary</b> to keep an eye on them</div>
                    <div className="cs-subjlist">
                      {tracks.map((tr, i) => {
                        const on = tr.id === reEdit.subjectTrackId;
                        const sec = (reEdit.secondaryTrackIds || []).includes(tr.id);
                        const s = tr.samples || [];
                        const span = s.length ? `${fmtTime(s[0].t)}–${fmtTime(s[s.length - 1].t)}` : '';
                        const avgArea = s.length ? s.reduce((a, p) => a + (p.w || 0) * (p.h || 0), 0) / s.length : 0;
                        const size = avgArea > 0.06 ? 'large' : avgArea > 0.02 ? 'medium' : 'small';
                        return (
                          <div
                            key={tr.id}
                            className={`cs-subjchip ${on ? 'on' : ''} ${sec ? 'sec' : ''}`}
                            style={{ borderColor: on ? trackColor(i) : sec ? trackColor(i) : undefined }}
                            onMouseEnter={() => setHoverTrackId(tr.id)}
                            onMouseLeave={() => setHoverTrackId((h) => (h === tr.id ? null : h))}
                          >
                            <span className="cs-subjchip-sw" style={{ background: trackColor(i) }} />
                            <span className="cs-subjchip-body">
                              <span className="cs-subjchip-title">Subject {i + 1}{on ? ' · primary' : sec ? ' · secondary' : ''}</span>
                              <span className="cs-subjchip-meta">{size} · {s.length} hits{span ? ` · ${span}` : ''}</span>
                            </span>
                            <span className="cs-subjchip-acts">
                              <button
                                type="button"
                                className={`cs-chip-act ${on ? 'on' : ''}`}
                                title="Make this the primary subject (the crop follows it)"
                                onClick={() => lockSubject(tr)}
                              >
                                ★
                              </button>
                              <button
                                type="button"
                                className={`cs-chip-act ${sec ? 'on' : ''}`}
                                disabled={on}
                                title={on ? 'This is the primary subject' : sec ? 'Remove from secondary' : 'Track as a secondary subject'}
                                onClick={() => toggleSecondary(tr.id)}
                              >
                                {sec ? '− 2nd' : '+ 2nd'}
                              </button>
                            </span>
                          </div>
                        );
                      })}
                      {!tracks.length && <span className="cs-help">No subjects detected — using center.</span>}
                    </div>
                    <div className="cs-help" style={{ marginTop: 8 }}>
                      Or click a colored box on the left to make that subject primary; click anywhere on the frame to
                      nudge the center at the current time. The teal rectangle is exactly what gets exported.
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <strong style={{ fontSize: 13 }}>Center path · {reEdit.waypoints.length} waypoints</strong>
                      </div>
                      <div className="cs-wp-track">
                        {dur > 0 && reEdit.waypoints.map((w) => (
                          <button
                            key={w.t}
                            className="cs-wp-marker"
                            style={{ left: `${(w.t / dur) * 100}%` }}
                            title={`${fmtTime(w.t)} — click to seek, shift-click to delete`}
                            onClick={(e) => (e.shiftKey ? removeWaypoint(w.t) : (videoRef.current && (videoRef.current.currentTime = w.t)))}
                          />
                        ))}
                        {dur > 0 && <div className="cs-wp-playhead" style={{ left: `${(t / dur) * 100}%` }} />}
                      </div>
                    </div>
                  </>
                )}
                </>)}

                {/* One export for the whole movie — each clip contributes its own
                    crop window (mode / zoom / center / subject) via cropSegments. */}
                <div style={{ marginTop: 16 }}>
                  <div className="cs-section-head" style={{ marginBottom: 6 }}><h2 style={{ fontSize: 15 }}>Export formats</h2></div>
                  <div className="cs-help" style={{ marginBottom: 8 }}>
                    Each clip is cropped with its own window; pick the output ratio per format below.
                  </div>
                  <SocialExportPanel
                    movieId={id}
                    movieTitle={movie.title}
                    subjectTrackId={reEdit.subjectTrackId}
                    waypoints={reEdit.waypoints}
                    cropScale={manual.scale}
                    cropSegments={cropSegments}
                    exports={exports}
                    previewAspect={aspect}
                    onFocusAspect={setAspect}
                    movieFps={movie?.render?.outputFps || movie?.output?.fps || 30}
                    hasExportMaster={movie?.exportMaster?.status === 'ready'}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
